import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { useResizeX } from 'Hooks/useResize';
import useWindowDimensions from 'Hooks/useWindowSize';
import { useIntl } from 'react-intl';
import { selectCloudAgents, selectSchedulerDetail, selectSelectedSection, selectUserAgents } from 'store/Schedule/selectors';
import { resetDetail } from 'store/Schedule/actions';
import StandardDropdown from 'Common/Components/StandarDropdown';
import Header from './Header';
import StyledCheckbox from 'Common/Components/StyledCheckbox';
import CronExpression from '../CreateEditSchedule/components/CronExpression';
import TagsSection from '../CreateEditSchedule/components/TagsSection';
import AgentsDropdown from '../CreateEditSchedule/components/AgentsDropdown';
import AgentParams from '../CreateEditSchedule/components/AgentsParams';
import ToolParams from 'Screens/Automation/Agents/components/AgentDetail/components/ToolParams';
import WorkspacesSelector from '../CreateEditSchedule/components/WorkspacesSelector';
import {
  ResizerWrapper, Resizer, Transparency
} from 'Screens/Contextualization/Host/components/HostDetail/styled';
import {
  Subtitle, WrapperAgent, AgentSection,
  DescriptionSection, Label, Description
} from 'Screens/Automation/Schedule/components/CreateEditSchedule/styled';
import { Wrapper, DetailWrapper, Content, StyledToolParams } from './styled';
import { STEP_VALUES } from 'Common/Components/SeverityRange/constants';
import ExecutionParams from 'Screens/Automation/Agents/components/AgentDetail/components/ExecutionParams';

const SchedulerDetail = () => {
  const dispatch = useDispatch();
  const [showTransparency, setShowTransparency] = useState(false);
  const ref = useRef(null);
  const wrapperRef = useRef(null);
  const resizerRef = useRef(null);
  const { width } = useWindowDimensions();
  const minWidth = width < 3000 ? width * 0.55 : width * 0.35;
  const maxWidth = width < 3000 ? width * 0.75 : width * 0.55;
  const wrapperWidth = useResizeX(wrapperRef, resizerRef, minWidth, minWidth, maxWidth);
  const schedule = useSelector(selectSchedulerDetail);
  const section = useSelector(selectSelectedSection);
  const userAgents = useSelector(selectUserAgents);
  const cloudAgents = useSelector(selectCloudAgents);
  const [dataToSave, setData] = useState({
    scheduleId: get(schedule, 'value.id', 0),
    crontab: {
      expression: get(schedule, 'value.crontab', ''),
      name: ''
    },
    active: get(schedule, 'value.active', false),
    description: get(schedule, 'value.description', ''),
    currentAgent: section === 'scheduledAgents'
      ? { label: get(schedule, 'value.agent.name', ''), value: { ...get(schedule, 'value.agent', {}) }, type: 'agent' }
      : { label: get(schedule, 'value.cloud_agent.name', ''), value: { ...get(schedule, 'value.cloud_agent', {}), type: 'cloud_agent' } },
    tool: '',
    parameters: get(schedule, 'value.parameters', {}),
    workspaces: get(schedule, 'value.workspaces', []),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    type: section === 'scheduledAgents' ? 'agent' : 'cloud_agent',
    targetError: '',
    severityRange: {
      min: { value: 0, severity: 'unclassified' },
      max: { value: 5, severity: 'critical' }
    }
  });
  const tools = get(dataToSave, 'currentAgent.value.executors', []);
  const mappedTools = tools.map((t) => ({ name: t.name, desc: t.name }));

  const intl = useIntl();
  const selectionLabelId = section === 'scheduledCloudAgents'
    ? 'schedule.createEditSchedule.securityScannerSelection'
    : 'schedule.createEditSchedule.runnerSelection';
  const dropdownPlaceholderId = section === 'scheduledCloudAgents'
    ? 'schedule.createEditSchedule.dropdownSecurityScanner.placeholder'
    : 'schedule.createEditSchedule.dropdownRunner.placeholder';

  const onScroll = () => {
    const scrollTop = get(ref, 'current.scrollTop', 0);
    if (scrollTop === 0) setShowTransparency(false);
    else setShowTransparency(true);
  };

  useEffect(() => {
    const toolName = get(schedule, 'value.executor.name', '');
    const tool = get(schedule, 'value.agent.executors')?.find((t) => t.name === toolName);
    const defaultCrontab = () => {
      let crontabName = '';
      const crontabValue = get(schedule, 'value.crontab', '');
      switch (crontabValue) {
        case '0 4 * * *':
          crontabName = 'day';
          break;
        case '0 4 * * 0':
          crontabName = 'week';
          break;
        case '0 4 1 * *':
          crontabName = 'month';
          break;
        default:
          crontabName = 'custom';
          break;
      }
      return { name: crontabName, expression: crontabValue };
    };
    const min = STEP_VALUES.find((sev) => sev.parsedSeverity.toUpperCase() === get(schedule, 'value.min_severity', 0));
    const max = STEP_VALUES.find((sev) => sev.parsedSeverity.toUpperCase() === get(schedule, 'value.max_severity', 5));
    setData({
      scheduleId: get(schedule, 'value.id', 0),
      crontab: defaultCrontab(),
      active: get(schedule, 'value.active', false),
      description: get(schedule, 'value.description', ''),
      currentAgent: section === 'scheduledAgents'
        ? { label: get(schedule, 'value.agent.name', ''), value: { ...get(schedule, 'value.agent', {}) }, type: 'agent' }
        : { label: get(schedule, 'value.cloud_agent.name', ''), value: { ...get(schedule, 'value.cloud_agent', {}), type: 'cloud_agent' } },
      tool,
      parameters: get(schedule, 'value.parameters', {}),
      workspaces: get(schedule, 'value.workspaces', []),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type: section === 'scheduledAgents' ? 'agent' : 'cloud_agent',
      targetError: '',
      severityRange: {
        min: { value: min?.value || 0, severity: min?.parsedSeverity || 'unclassified' },
        max: { value: max?.value || 5, severity: max?.parsedSeverity || 'critical' }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, section]);

  useEffect(() => () => {
    dispatch(resetDetail());
  }, [dispatch]);

  const onChange = (field, value) => {
    const newData = { ...dataToSave, [field]: value };
    setData(newData);
  };
  const parsedAgents = () => {
    if ((section === 'scheduledAgents') && !isEmpty(userAgents)) return userAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else if ((section === 'scheduledCloudAgents') && !isEmpty(cloudAgents)) return cloudAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else return [];
  };

  const onChangeAgent = (obj) => {
    const newAgent = { ...obj, type: section === 'scheduledAgents' ? 'agent' : 'cloud_agent' };
    const newData = { ...dataToSave, currentAgent: newAgent, tool: {}, parameters: {} };
    setData(newData);
  };

  const onChangeField = (field, value) => {
    const newParams = { ...dataToSave.parameters };
    newParams[field] = value;
    if (!trim(value)) delete newParams[field];
    onChange('parameters', newParams);
  };

  const validateUrlTarget = (domain) => {
    // eslint-disable-next-line prefer-regex-literals
    const re = new RegExp(/^(?!:\/\/)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/);
    if (domain && (!re.test(domain))) onChange('targetError', 'Target should be a domain. e.g.: domain.com');
    else onChange('targetError', '');
  };

  const onChangeFieldCloud = (field, value, type) => {
    if (type === 'url') validateUrlTarget(value);
    const newParams = { ...dataToSave.parameters, [field]: value };
    if (type === 'list') {
      const newValue = value.replace(/\s+/g, '');
      newParams[field] = newValue.split(',');
    }
    if (value === '') delete newParams[field];
    onChange('parameters', newParams);
  };

  const onChangeTool = (field, value) => {
    const tool = get(dataToSave, 'currentAgent.value.executors', get(schedule, 'value.agent.executors')).find((t) => t.name === value);
    const newData = { ...dataToSave, tool, parameters: {} };
    setData(newData);
  };

  const setSeverityRange = (values) => onChange('severityRange', values);

  return !isEmpty(schedule) && (
    <Wrapper ref={ wrapperRef } wrapperWidth={ wrapperWidth }>
      <ResizerWrapper ref={ resizerRef } children={ <Resizer /> } />
      <DetailWrapper>
        <Header dataToSave={ dataToSave } />
        { showTransparency && <Transparency /> }
        <Content tabRef={ ref } onScroll={ onScroll } showTransparency={ showTransparency }>
          <DescriptionSection>
            <Label>{ intl.formatMessage({ id: 'schedule.createEditSchedule.description' }) }</Label>
            <Description id="scheduler-modal-description" onChange={ (e) => onChange('description', e.target.value) } value={ dataToSave.description } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.description.placeholder' }) } />
            <StyledCheckbox id="scheduler-modal-active" checked={ dataToSave.active } label="Active" onChange={ () => { onChange('active', !dataToSave.active); } } />
          </DescriptionSection>

          <CronExpression crontab={ dataToSave.crontab } onChangeCrontab={ (value) => onChange('crontab', value) } />

          <AgentSection>
            <Subtitle>{ intl.formatMessage({ id: selectionLabelId }) }</Subtitle>
            <WrapperAgent>
              <AgentsDropdown id="scheduler-modal-agent" options={ parsedAgents() } updateValue={ onChangeAgent } value={ dataToSave.currentAgent } placeholder={ intl.formatMessage({ id: dropdownPlaceholderId }) } />
            </WrapperAgent>

            { (!isEmpty(dataToSave.currentAgent) || !isEmpty(get(schedule, 'value.agent', null))) &&
              (<>

              { section === 'scheduledCloudAgents'
                ? <>
                  <StyledToolParams isCloud>
                      <ExecutionParams key={ `${dataToSave.scheduleId}-execution` } currentTool={ dataToSave.currentAgent } cloudScheduler />
                      <AgentParams key={ `${dataToSave.scheduleId}-agent` } currentAgent={ dataToSave.currentAgent } onChangeField={ onChangeFieldCloud } targetError={ dataToSave.targetError } defaultParams={ dataToSave.parameters } />
                  </StyledToolParams>
                  </>
                : <>
                    <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.toolSelection' }) }</Subtitle>
                    <StandardDropdown key={ dataToSave.scheduleId } id="scheduler-modal-tool" defaultValue={ get(dataToSave, 'tool.name', '') } width="200px" field="toolParams" options={ mappedTools } updateValue={ onChangeTool } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownTool.placeholder' }) } />
                    { dataToSave?.tool?.name
                      ? <StyledToolParams key={ dataToSave.scheduleId }>
                          <ToolParams
                            currentTool={ dataToSave.tool }
                            defaultParams={ dataToSave.parameters }
                            onChangeField={ onChangeField }
                            severityRange={ dataToSave.severityRange }
                            setSeverityRange={ setSeverityRange }
                          />
                        </StyledToolParams>
                      : null
                    }
                  </>
                }

               </>
              ) }
          </AgentSection>
          <WorkspacesSelector scheduleWorkspaces={ dataToSave.workspaces } setScheduleWorkspaces={ (items) => onChange('workspaces', items) } />
          <TagsSection />
        </Content>
      </DetailWrapper>
    </Wrapper>
  );
};

export default SchedulerDetail;
